import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon } from '@mui/icons-material';
import {
  Employee,
  LEAD_ALERT_LABELS,
  LEAD_CHANNEL_LABELS,
  LEAD_PAYMENT_METHOD_LABELS,
  LEAD_PRICE_RANGE_LABELS,
  LEAD_STATUS_LABELS,
  Lead,
  LeadAlert,
  LeadChannel,
  LeadPayload,
  LeadPaymentMethod,
  LeadPriceRange,
  LeadStatus,
} from '../../types';
import {
  buildLeadWhatsappMessage,
  formatPhoneBR,
  getWhatsappLink,
  onlyDigits,
  todayISODate,
} from './leadUtils';

const STEPS = [
  'Identificação',
  'Origem',
  'Interesse',
  'Funil',
  'Gestão',
  'Pós-venda',
  'Observações',
  'Ações',
] as const;

const STATUS_ORDER: LeadStatus[] = [
  'novo',
  'em_atendimento',
  'negociacao',
  'proposta',
  'ganho',
  'perdido',
  'pos_venda',
];

export type LeadFormValues = LeadPayload;

const emptyLead = (): LeadFormValues => ({
  name: '',
  whatsapp: '',
  email: '',
  city_neighborhood: '',
  first_contact_at: todayISODate(),
  origin: null,
  secondary_origin: null,
  current_channel: null,
  vehicle_interest: '',
  price_range: null,
  has_trade_in: false,
  trade_in_vehicle: '',
  payment_method: null,
  status: 'novo',
  seller_id: null,
  last_contact_at: '',
  next_contact_at: '',
  next_contact_action: '',
  alert: 'nenhum',
  purchased_vehicle: '',
  purchase_date: '',
  trade_forecast_or_birthday: '',
  notes: '',
});

const leadToForm = (lead: Lead): LeadFormValues => ({
  name: lead.name ?? '',
  whatsapp: lead.whatsapp ?? '',
  email: lead.email ?? '',
  city_neighborhood: lead.city_neighborhood ?? '',
  first_contact_at: lead.first_contact_at?.slice(0, 10) ?? todayISODate(),
  origin: lead.origin ?? null,
  secondary_origin: lead.secondary_origin ?? null,
  current_channel: lead.current_channel ?? null,
  vehicle_interest: lead.vehicle_interest ?? '',
  price_range: lead.price_range ?? null,
  has_trade_in: Boolean(lead.has_trade_in),
  trade_in_vehicle: lead.trade_in_vehicle ?? '',
  payment_method: lead.payment_method ?? null,
  status: lead.status ?? 'novo',
  seller_id: lead.seller_id ?? null,
  last_contact_at: lead.last_contact_at?.slice(0, 10) ?? '',
  next_contact_at: lead.next_contact_at?.slice(0, 10) ?? '',
  next_contact_action: lead.next_contact_action ?? '',
  alert: lead.alert ?? 'nenhum',
  purchased_vehicle: lead.purchased_vehicle ?? '',
  purchase_date: lead.purchase_date?.slice(0, 10) ?? '',
  trade_forecast_or_birthday: lead.trade_forecast_or_birthday?.slice(0, 10) ?? '',
  notes: lead.notes ?? '',
});

export interface LeadFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: LeadFormValues) => Promise<void> | void;
  lead?: Lead | null;
  sellers: Employee[];
  saving?: boolean;
}

const LeadFormModal: React.FC<LeadFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  lead = null,
  sellers,
  saving = false,
}) => {
  const isEdit = Boolean(lead?.id);
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<LeadFormValues>(emptyLead);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const reset = useCallback(() => {
    setForm(lead ? leadToForm(lead) : emptyLead());
    setActiveStep(0);
    setErrors({});
  }, [lead]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const setField = <K extends keyof LeadFormValues>(key: K, value: LeadFormValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key as string]) return prev;
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  const validateStep = (step: number): boolean => {
    const nextErrors: Record<string, string> = {};
    if (step === 0) {
      if (!form.name.trim()) nextErrors.name = 'Informe o nome';
      if (onlyDigits(form.whatsapp).length < 10) nextErrors.whatsapp = 'Informe um WhatsApp válido';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleSave = async () => {
    if (!validateStep(0)) {
      setActiveStep(0);
      return;
    }
    const payload: LeadFormValues = {
      ...form,
      name: form.name.trim(),
      whatsapp: onlyDigits(form.whatsapp),
      email: form.email?.trim() || null,
      city_neighborhood: form.city_neighborhood?.trim() || null,
      first_contact_at: form.first_contact_at || null,
      vehicle_interest: form.vehicle_interest?.trim() || null,
      trade_in_vehicle: form.has_trade_in ? form.trade_in_vehicle?.trim() || null : null,
      last_contact_at: form.last_contact_at || null,
      next_contact_at: form.next_contact_at || null,
      next_contact_action: form.next_contact_action?.trim() || null,
      purchased_vehicle: form.purchased_vehicle?.trim() || null,
      purchase_date: form.purchase_date || null,
      trade_forecast_or_birthday: form.trade_forecast_or_birthday || null,
      notes: form.notes?.trim() || null,
      seller_id: form.seller_id || null,
    };
    await onSubmit(payload);
  };

  const advanceStatus = () => {
    const idx = STATUS_ORDER.indexOf(form.status);
    if (idx < 0 || idx >= STATUS_ORDER.length - 1) return;
    const next = STATUS_ORDER[idx + 1];
    if (next === 'perdido') {
      setField('status', 'ganho');
      return;
    }
    setField('status', next);
  };

  const channelOptions = useMemo(() => Object.keys(LEAD_CHANNEL_LABELS) as LeadChannel[], []);

  const renderChannelSelect = (
    label: string,
    key: 'origin' | 'secondary_origin' | 'current_channel'
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={form[key] ?? ''}
        onChange={(e: SelectChangeEvent) =>
          setField(key, (e.target.value || null) as LeadChannel | null)
        }
      >
        <MenuItem value="">—</MenuItem>
        {channelOptions.map((c) => (
          <MenuItem key={c} value={c}>
            {LEAD_CHANNEL_LABELS[c]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const waHref = getWhatsappLink(onlyDigits(form.whatsapp), buildLeadWhatsappMessage(form));

  const stepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome *"
                fullWidth
                size="small"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="WhatsApp *"
                fullWidth
                size="small"
                value={formatPhoneBR(form.whatsapp)}
                onChange={(e) => setField('whatsapp', onlyDigits(e.target.value))}
                error={Boolean(errors.whatsapp)}
                helperText={errors.whatsapp}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="E-mail"
                fullWidth
                size="small"
                type="email"
                value={form.email ?? ''}
                onChange={(e) => setField('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cidade / bairro"
                fullWidth
                size="small"
                value={form.city_neighborhood ?? ''}
                onChange={(e) => setField('city_neighborhood', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data do primeiro contato"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.first_contact_at ?? ''}
                onChange={(e) => setField('first_contact_at', e.target.value)}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {renderChannelSelect('Origem', 'origin')}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderChannelSelect('Origem secundária', 'secondary_origin')}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderChannelSelect('Canal atual de atendimento', 'current_channel')}
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Veículo de interesse"
                fullWidth
                size="small"
                value={form.vehicle_interest ?? ''}
                onChange={(e) => setField('vehicle_interest', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Faixa de preço</InputLabel>
                <Select
                  label="Faixa de preço"
                  value={form.price_range ?? ''}
                  onChange={(e) =>
                    setField('price_range', (e.target.value || null) as LeadPriceRange | null)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {(Object.keys(LEAD_PRICE_RANGE_LABELS) as LeadPriceRange[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {LEAD_PRICE_RANGE_LABELS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de pagamento</InputLabel>
                <Select
                  label="Forma de pagamento"
                  value={form.payment_method ?? ''}
                  onChange={(e) =>
                    setField('payment_method', (e.target.value || null) as LeadPaymentMethod | null)
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {(Object.keys(LEAD_PAYMENT_METHOD_LABELS) as LeadPaymentMethod[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {LEAD_PAYMENT_METHOD_LABELS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.has_trade_in)}
                    onChange={(e) => setField('has_trade_in', e.target.checked)}
                  />
                }
                label="Terá troca"
              />
            </Grid>
            {form.has_trade_in ? (
              <Grid item xs={12}>
                <TextField
                  label="Veículo da troca"
                  fullWidth
                  size="small"
                  value={form.trade_in_vehicle ?? ''}
                  onChange={(e) => setField('trade_in_vehicle', e.target.value)}
                />
              </Grid>
            ) : null}
          </Grid>
        );
      case 3:
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setField('status', e.target.value as LeadStatus)}
            >
              {(Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {LEAD_STATUS_LABELS[k]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 4:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Vendedor</InputLabel>
                <Select
                  label="Vendedor"
                  value={form.seller_id ?? ''}
                  onChange={(e) =>
                    setField('seller_id', e.target.value === '' ? null : Number(e.target.value))
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {sellers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Alerta</InputLabel>
                <Select
                  label="Alerta"
                  value={form.alert ?? 'nenhum'}
                  onChange={(e) => setField('alert', e.target.value as LeadAlert)}
                >
                  {(Object.keys(LEAD_ALERT_LABELS) as LeadAlert[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {LEAD_ALERT_LABELS[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Último contato"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.last_contact_at ?? ''}
                onChange={(e) => setField('last_contact_at', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Próximo contato"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.next_contact_at ?? ''}
                onChange={(e) => setField('next_contact_at', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Ação do próximo contato"
                fullWidth
                size="small"
                value={form.next_contact_action ?? ''}
                onChange={(e) => setField('next_contact_action', e.target.value)}
              />
            </Grid>
          </Grid>
        );
      case 5:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Veículo comprado"
                fullWidth
                size="small"
                value={form.purchased_vehicle ?? ''}
                onChange={(e) => setField('purchased_vehicle', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Data da compra"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.purchase_date ?? ''}
                onChange={(e) => setField('purchase_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Previsão da troca / aniversário"
                fullWidth
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.trade_forecast_or_birthday ?? ''}
                onChange={(e) => setField('trade_forecast_or_birthday', e.target.value)}
              />
            </Grid>
          </Grid>
        );
      case 6:
        return (
          <TextField
            label="Observações"
            fullWidth
            multiline
            minRows={5}
            size="small"
            value={form.notes ?? ''}
            onChange={(e) => setField('notes', e.target.value)}
          />
        );
      case 7:
        return (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Atalhos rápidos antes de salvar o lead.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                color="success"
                startIcon={<WhatsAppIcon />}
                component="a"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                disabled={onlyDigits(form.whatsapp).length < 10}
              >
                Abrir WhatsApp
              </Button>
              <Button variant="outlined" onClick={() => setField('last_contact_at', todayISODate())}>
                Marcar último contato = hoje
              </Button>
              <Button variant="outlined" onClick={advanceStatus}>
                Avançar status
              </Button>
            </Stack>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status atual
              </Typography>
              <Typography fontWeight={600}>{LEAD_STATUS_LABELS[form.status]}</Typography>
            </Box>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? 'Editar lead' : 'Novo lead'}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, pt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ minHeight: 220 }}>{stepContent()}</Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleBack} disabled={activeStep === 0 || saving}>
            Voltar
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button variant="contained" onClick={handleNext} disabled={saving}>
              Próximo
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default LeadFormModal;
